import { ui } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { FormViewT } from "@/types/database/forms";
import { Link } from "@tanstack/react-router";
import {
    type ColumnDef,
    type ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { SlidersHorizontal } from "lucide-react";
import * as React from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import MultiSelect from "../../../components/form/MultiSelect";
import { PrevalenceBadge } from "./PrevalenceBadge";
import { ScoringBadge } from "./ScoringBadge";

// Table columns definition
const columns: ColumnDef<FormViewT>[] = [
    {
        accessorKey: "form_name",
        header: "Name",
        cell: (info) => info.getValue(),
        enableSorting: true,
    },
    {
        accessorKey: "form_description",
        header: "Description",
        cell: (info) => (
            <span className="block w-60 overflow-hidden text-ellipsis line-clamp-3">
                {info.getValue()}
            </span>
        ),
        enableSorting: true,
    },
    {
        accessorKey: "is_published",
        header: "Published",
        cell: (info) => (info.getValue() ? "Yes" : "No"),
        enableSorting: true,
    },
    {
        id: "number_of_pages",
        header: "Pages",
        accessorFn: (row) => Array.isArray(row.pages) ? row.pages.length : 0,
        enableSorting: true,
        cell: (info) => info.getValue() || "-",
    },
    {
        id: "number_of_questions",
        header: "Questions",
        accessorFn: (row) =>
            Array.isArray(row.pages)
                ? (row.pages as unknown[]).reduce(
                    (acc, page) =>
                        Array.isArray((page as { inputs?: unknown[] })?.inputs)
                            ? acc +
                                ((page as { inputs?: unknown[] })?.inputs
                                    ?.length ?? 0)
                            : acc,
                    0,
                )
                : 0,
        enableSorting: true,
        cell: (info) => info.getValue() || "-",
    },
    {
        accessorKey: "orthopaedic_areas",
        header: "Orthopaedic Areas",
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) =>
            Array.isArray(info.getValue())
                ? (
                    <div className="flex flex-wrap gap-1">
                        {(info.getValue() as string[]).map((area) => (
                            <Badge key={area} color="cyan">{area}</Badge>
                        ))}
                    </div>
                )
                : null,
        filterFn: (row, columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return true;
            }
            const value = row.getValue(columnId);
            if (Array.isArray(value)) {
                return value.some((v) => filterValue.includes(v));
            }
            return false;
        },
    },
    {
        accessorKey: "prevalence",
        header: "Prevalence",
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => (
            <PrevalenceBadge prevalence={info.getValue() as string} />
        ),
        filterFn: (row, columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return true;
            }
            const value = row.getValue(columnId);
            return filterValue.includes(value);
        },
    },
    {
        accessorKey: "scoring",
        header: "Scoring",
        enableSorting: true,
        cell: (info) => (
            <div className="flex flex-col gap-1">
                <ScoringBadge scoring={info.row.original.scoring} />
            </div>
        ),
    },
    {
        accessorKey: "time_to_complete",
        header: "Time to Complete",
        enableSorting: true,
        cell: (info) => info.getValue() ? `${info.getValue()} min` : "-",
    },
    {
        accessorKey: "form_instructions",
        header: "Instructions",
        enableSorting: false,
        cell: (info) => (
            <span className="block w-60 overflow-hidden text-ellipsis line-clamp-3">
                {info.getValue() || "-"}
            </span>
        ),
    },
    {
        id: "link",
        header: "Link",
        cell: ({ row }) => (
            <Link
                to={`/public/form/${row.original.form_id}`}
                className="text-blue-600 hover:text-blue-800 underline"
            >
                View
            </Link>
        ),
        enableSorting: false,
    },
];

function useFormsTable(forms: FormViewT[]) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<
        ColumnFiltersState
    >([]);

    // For filter UI
    const allPrevalence = React.useMemo(() => {
        const set = new Set<string>();
        for (const form of forms) {
            if (
                typeof (form as { prevalence?: string }).prevalence === "string"
            ) {
                set.add((form as { prevalence: string }).prevalence);
            }
        }
        return Array.from(set);
    }, [forms]);
    const allAreas = React.useMemo(() => {
        const set = new Set<string>();
        for (const form of forms) {
            if (
                Array.isArray(
                    (form as { orthopaedic_areas?: string[] })
                        .orthopaedic_areas,
                )
            ) {
                for (
                    const area of (form as { orthopaedic_areas: string[] })
                        .orthopaedic_areas
                ) {
                    if (typeof area === "string") set.add(area);
                }
            }
        }
        return Array.from(set);
    }, [forms]);

    const table = useReactTable({
        data: forms,
        columns,
        state: { sorting, columnFilters },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        debugTable: false,
    });

    return {
        table,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        allPrevalence,
        allAreas,
    };
}

interface FilterFormT {
    prevalence: string[];
    orthopaedic_areas: string[];
}

function FormsTableFilterPopover({
    allPrevalence,
    allAreas,
    onSave,
    initialPrevalence,
    initialAreas,
}: {
    allPrevalence: string[];
    allAreas: string[];
    onSave: (filters: FilterFormT) => void;
    initialPrevalence: string[];
    initialAreas: string[];
}) {
    const form = useForm<FilterFormT>({
        defaultValues: {
            prevalence: initialPrevalence,
            orthopaedic_areas: initialAreas,
        },
    });
    const handleSubmit: SubmitHandler<FilterFormT> = (data) => {
        onSave(data);
    };
    return (
        <ui.Popover>
            <ui.Button asChild variant="outline" size="sm">
                <ui.PopoverTrigger>
                    <SlidersHorizontal size={20} />
                    <span className="sr-only md:not-sr-only">Filter</span>
                </ui.PopoverTrigger>
            </ui.Button>
            <ui.PopoverContent align="end" className="flex flex-col gap-2">
                <h3 className="text-md text-slate-900 dark:text-slate-100">
                    Filter Forms
                </h3>
                <ui.Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className="flex flex-col gap-y-0 space-y-2"
                    >
                        <div className="space-y-1">
                            <ui.Label>Prevalence</ui.Label>

                            <MultiSelect
                                name="prevalence"
                                options={allPrevalence.map((p) => ({
                                    value: p,
                                    label: p,
                                }))}
                                trigger="Select Prevalence"
                            />
                        </div>
                        <div className="space-y-1">
                            <ui.Label>Orthopaedic Areas</ui.Label>
                            <MultiSelect
                                name="orthopaedic_areas"
                                options={allAreas.map((a) => ({
                                    value: a,
                                    label: a,
                                }))}
                                trigger="Select Areas"
                            />
                        </div>
                        <ui.Button type="submit" size="sm">Save</ui.Button>
                    </form>
                </ui.Form>
            </ui.PopoverContent>
        </ui.Popover>
    );
}

export function FormsTable({ forms }: { forms: FormViewT[] }) {
    const { table, columnFilters, setColumnFilters, allPrevalence, allAreas } =
        useFormsTable(forms);

    // Filter UI for prevalence
    const prevalenceFilter = table.getColumn("prevalence");
    // Filter UI for orthopaedic_areas
    const areasFilter = table.getColumn("orthopaedic_areas");

    const [filterState, setFilterState] = React.useState<FilterFormT>({
        prevalence: [],
        orthopaedic_areas: [],
    });

    // Update table filters when filterState changes
    React.useEffect(() => {
        if (prevalenceFilter) {
            prevalenceFilter.setFilterValue(
                filterState.prevalence.length
                    ? filterState.prevalence
                    : undefined,
            );
        }
        if (areasFilter) {
            areasFilter.setFilterValue(
                filterState.orthopaedic_areas.length
                    ? filterState.orthopaedic_areas
                    : undefined,
            );
        }
    }, [filterState, prevalenceFilter, areasFilter]);

    return (
        <>
            <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-4 mb-2">
                <h2 className="font-bold text-2xl tracking-tight">
                    All Forms
                </h2>
                <FormsTableFilterPopover
                    allPrevalence={allPrevalence}
                    allAreas={allAreas}
                    initialPrevalence={filterState.prevalence}
                    initialAreas={filterState.orthopaedic_areas}
                    onSave={setFilterState}
                />
            </div>
            <div className="bg-white border border-slate-100 rounded-lg overflow-x-auto">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow
                                key={headerGroup.id}
                                className="top-0 z-10 sticky bg-slate-50"
                            >
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="cursor-pointer select-none"
                                        onClick={header.column.getCanSort()
                                            ? header.column
                                                .getToggleSortingHandler()
                                            : undefined}
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                        {header.column.getIsSorted() ===
                                                "asc" &&
                                            " ▲"}
                                        {header.column.getIsSorted() ===
                                                "desc" &&
                                            " ▼"}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.length === 0
                            ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="text-center"
                                    >
                                        No forms found
                                    </TableCell>
                                </TableRow>
                            )
                            : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((
                                            cell,
                                        ) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef
                                                        .cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}

export default FormsTable;
